import { useState, useEffect, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import { apiGet, apiSend, errorMessage } from "./api";
import "./BillingTest.css";

// IBAN de test Stripe. Le premier suffit pour un parcours nominal ; les autres
// servent à provoquer un échec ou un litige sans attendre d'incident réel.
const TEST_IBANS = [
  { iban: "DE89370400440532013000", effect: "Prélèvement accepté" },
  { iban: "DE62370400440532013001", effect: "Prélèvement refusé" },
  { iban: "DE35370400440532013002", effect: "Accepté puis contesté" },
];

function formatDateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Flag({ ok, children }) {
  return (
    <span className={`bt-flag${ok ? " bt-flag-ok" : " bt-flag-ko"}`}>
      {ok ? "✓" : "✗"} {children}
    </span>
  );
}

function BillingTest({ user, onNavigate, onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState("");
  const [planId, setPlanId] = useState("premium");
  const [billing, setBilling] = useState("monthly");
  const [starting, setStarting] = useState(false);
  const [lastUrl, setLastUrl] = useState("");

  const fetchOverview = useCallback(async () => {
    try {
      const res = await apiGet("/api/billing/admin/overview",
        "Impossible de charger l'état de la facturation");
      setData(res);
      setError("");
      // Présélectionne le premier commerce pour éviter un aller-retour
      setOrgId((prev) => prev || (res.organizations[0]?.id ?? ""));
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger l'état de la facturation"));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Ouvre Stripe dans un nouvel onglet : la page de test reste consultable
  // pendant le parcours de paiement, pour suivre les événements qui arrivent.
  const startSession = async () => {
    setStarting(true);
    setError("");
    setLastUrl("");
    try {
      const res = await apiSend("/api/billing/checkout-session", "POST",
        { plan_id: planId, billing, organization_id: parseInt(orgId) },
        "Impossible de créer la session");
      setLastUrl(res.url);
      window.open(res.url, "_blank", "noopener");
    } catch (e) {
      setError(errorMessage(e, "Impossible de créer la session"));
    }
    setStarting(false);
  };

  return (
    <div className="bt-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Test paiement"
        onBack={() => onNavigate("admin")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      >
        <button className="rc-btn rc-btn-outline site-header-btn" onClick={fetchOverview}>
          Rafraîchir
        </button>
      </GestionHeader>

      <main className="bt-main">
        {error && <div className="bt-error">{error}</div>}

        {loading ? (
          <p className="bt-empty">Chargement...</p>
        ) : !data ? null : (
          <>
            <section className="bt-panel">
              <h2>Configuration</h2>
              <div className="bt-flags">
                <Flag ok={data.secret_key_set}>Clé secrète</Flag>
                <Flag ok={data.webhook_secret_set}>Secret webhook</Flag>
                <Flag ok={data.test_mode}>
                  {data.test_mode ? "Mode test" : "MODE PRODUCTION"}
                </Flag>
              </div>

              {data.account_error && (
                <p className="bt-error">Stripe : {data.account_error}</p>
              )}

              <dl className="bt-defs">
                <dt>Compte</dt>
                <dd>{data.account ? data.account.id : "—"}</dd>
                <dt>Pays / devise</dt>
                <dd>
                  {data.account
                    ? `${data.account.country} · ${data.account.currency.toUpperCase()}`
                    : "—"}
                </dd>
                <dt>URL du webhook</dt>
                <dd className="bt-mono">{data.webhook_url}</dd>
              </dl>

              {data.account && !data.account.charges_enabled && (
                <p className="bt-note">
                  <strong>charges_enabled : false</strong> — normal en mode test.
                  Cet indicateur ne passe à vrai qu'après vérification de
                  l'entreprise, et ne bloque pas les paiements de test.
                </p>
              )}
            </section>

            <section className="bt-panel">
              <h2>Lancer un paiement de test</h2>
              <div className="bt-form">
                <label>
                  Commerce
                  <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                    {data.organizations.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Offre
                  <select value={planId} onChange={(e) => setPlanId(e.target.value)}>
                    {data.plans.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Périodicité
                  <select value={billing} onChange={(e) => setBilling(e.target.value)}>
                    <option value="monthly">Mensuelle</option>
                    <option value="annual">Annuelle</option>
                  </select>
                </label>
                <button
                  className="rc-btn rc-btn-primary bt-start"
                  onClick={startSession}
                  disabled={starting || !orgId || !data.secret_key_set}
                >
                  {starting ? "Création..." : "Ouvrir Stripe Checkout"}
                </button>
              </div>

              {lastUrl && (
                <p className="bt-note">
                  Session ouverte dans un nouvel onglet. Si le navigateur l'a
                  bloqué : <a href={lastUrl} target="_blank" rel="noopener noreferrer">ouvrir manuellement</a>.
                </p>
              )}

              <table className="bt-table bt-ibans">
                <thead>
                  <tr>
                    <th>IBAN de test</th>
                    <th>Résultat simulé</th>
                  </tr>
                </thead>
                <tbody>
                  {TEST_IBANS.map((t) => (
                    <tr key={t.iban}>
                      <td className="bt-mono">{t.iban}</td>
                      <td>{t.effect}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="bt-note">
                En SEPA, signer le mandat ne vaut pas paiement : l'offre ne
                s'active qu'à réception de <code>invoice.paid</code>, quelques
                minutes plus tard en mode test.
              </p>
            </section>

            <section className="bt-panel">
              <h2>Facturation par commerce</h2>
              <div className="bt-table-scroll">
                <table className="bt-table">
                  <thead>
                    <tr>
                      <th>Commerce</th>
                      <th>Client Stripe</th>
                      <th>Abonnement</th>
                      <th>Statut</th>
                      <th>Paiement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.organizations.map((o) => (
                      <tr key={o.id}>
                        <td>{o.name}</td>
                        <td className="bt-mono">{o.stripe_customer_id || "—"}</td>
                        <td className="bt-mono">{o.stripe_subscription_id || "—"}</td>
                        <td>{o.subscription_status || "—"}</td>
                        <td>
                          <span className={`bt-pay${o.payment_default ? " bt-pay-late" : ""}`}>
                            {o.payment_default ? "En défaut" : "À jour"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bt-panel">
              <h2>
                Événements reçus
                <span className="bt-count">{data.events.length}</span>
              </h2>
              {data.events.length === 0 ? (
                <p className="bt-empty">
                  Aucun événement reçu pour l'instant. Ils apparaîtront ici dès
                  que Stripe aura appelé le webhook.
                </p>
              ) : (
                <div className="bt-table-scroll">
                  <table className="bt-table">
                    <thead>
                      <tr>
                        <th>Reçu le</th>
                        <th>Type</th>
                        <th>Commerce</th>
                        <th>Identifiant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.events.map((e) => (
                        <tr key={e.event_id}>
                          <td>{formatDateTime(e.received_at)}</td>
                          <td className="bt-mono">{e.type}</td>
                          <td>{e.organization || "—"}</td>
                          <td className="bt-mono bt-dim">{e.event_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="bt-note">
                Seuls les 20 derniers sont listés. Un événement déjà reçu est
                acquitté sans être rejoué : c'est cette table qui assure
                l'idempotence des webhooks.
              </p>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export default BillingTest;
